import * as uuidv4 from "uuid/v4";

import {
    createEllipsePath, createEllipseRandom,
    createLinePath,
    createLineRandom,
    createRectPath,
    createRectRandom, createSectionPath,
    LineRandom,
    RectRandom
} from "./path-data";
import { DEFAULT_FONT_SIZE, EPSILON } from "../constants";

export interface Point {
    readonly x: number,
    readonly y: number,
}

export type ShapeType = 'LINE' | 'ARROW' | 'RECT' | 'ELLIPSE' | 'FREEFORM' | 'TEXT';

export interface BaseShape {
    readonly type: ShapeType,
    readonly uuid: string,
    readonly x1: number,
    readonly y1: number,
    readonly x2: number,
    readonly y2: number,
    readonly path: string,
}

export interface LineShape extends BaseShape {
    readonly type: 'LINE',
    readonly random: LineRandom,
}

export interface ArrowShape extends BaseShape {
    readonly type: 'ARROW',
    readonly mainRandom: LineRandom,
    readonly arm1Random: LineRandom,
    readonly arm2Random: LineRandom,
}

export interface RectShape extends BaseShape {
    readonly type: 'RECT',
    readonly random: RectRandom,
}

export interface EllipseShape extends BaseShape {
    readonly type: 'ELLIPSE',
    readonly random: ReadonlyArray<number>,
}

export interface FreeformShape extends BaseShape {
    readonly type: 'FREEFORM',
    readonly points: ReadonlyArray<Point>,
}

export enum BoundsType { FIXED = 'FIXED', DYNAMIC = 'DYNAMIC' }

export interface TextShape extends BaseShape {
    readonly type: 'TEXT',
    readonly text: string,
    readonly lines: ReadonlyArray<string>,
    readonly size: number,
    readonly boundsType: BoundsType,
    readonly rotation: number,
}

export type ShapeData = LineShape | ArrowShape | RectShape | EllipseShape | FreeformShape | TextShape;


export function createShape(type: ShapeType, x: number, y: number): ShapeData {
    switch(type) {
        default:
            return createLine(x, y);
        case 'ARROW':
            return createArrow(x, y);
        case 'RECT':
            return createRect(x, y);
        case 'ELLIPSE':
            return createEllipse(x, y);
        case 'FREEFORM':
            return createFreehand(x, y);
        case 'TEXT':
            return createText(x, y);
    }
}

function createLine(x: number, y: number): LineShape {
    return {
        type: 'LINE',
        uuid: uuidv4(),
        x1: x,
        y1: y,
        x2: x,
        y2: y,
        path: '',
        random: createLineRandom(),
    }
}

function createArrow(x: number, y: number): ArrowShape {
    return {
        type: 'ARROW',
        uuid: uuidv4(),
        x1: x,
        y1: y,
        x2: x,
        y2: y,
        path: '',
        mainRandom: createLineRandom(),
        arm1Random: createLineRandom(),
        arm2Random: createLineRandom(),
    }
}

function createRect(x: number, y: number): RectShape {
    return {
        type: 'RECT',
        uuid: uuidv4(),
        x1: x,
        y1: y,
        x2: x,
        y2: y,
        path: '',
        random: createRectRandom(),
    }
}

function createEllipse(x: number, y: number): EllipseShape {
    return {
        type: 'ELLIPSE',
        uuid: uuidv4(),
        x1: x,
        y1: y,
        x2: x,
        y2: y,
        path: '',
        random: createEllipseRandom(),
    }
}

function createFreehand(x: number, y:number): FreeformShape {
    return {
        type: 'FREEFORM',
        uuid: uuidv4(),
        x1: x,
        y1: y,
        x2: x,
        y2: y,
        path: `M0 0`,
        points: [{x: 0, y: 0}],
    }
}

function createText(x: number, y: number): TextShape {
    return {
        type: 'TEXT',
        uuid: uuidv4(),
        x1: x,
        y1: y,
        x2: x,
        y2: y,
        path: '',
        text: '',
        lines: [],
        size: DEFAULT_FONT_SIZE,
        boundsType: BoundsType.DYNAMIC,
        rotation: 10 * Math.random() - 5,
    }
}

export function createPath(shape: ShapeData, x1: number, y1: number, x2: number, y2: number): string {
    switch (shape.type) {
        case 'LINE':
            return createLinePath(0, 0, x2 - x1, y2 - y1, shape.random, false);
        case 'ARROW':
            return createArrowPath(0, 0, x2 - x1, y2 - y1, shape.mainRandom, shape.arm1Random, shape.arm2Random);
        case 'RECT':
            return createRectPath(0, 0, x2 - x1, y2 - y1, shape.random);
        case 'ELLIPSE':
            return createEllipsePath(0, 0, x2 - x1, y2 - y1, shape.random);
        default:
            return '';
    }
}

export function addPoint(shape: FreeformShape, x: number, y: number) {
    let { points, x1, y1, x2, y2 } = shape;
    if (x < x1) {
        const delta = x1 - x;

        console.log(x1, x, delta);

        points = points.map(p => ({ x: p.x + delta, y: p.y }));
        x1 = x;

        console.log("New x1", x1);

    }
    x2 = Math.max(x, x2);
    if (y < y1) {
        const delta = y1 - y;
        points = points.map(p => ({ x: p.x, y: p.y + delta }));
        y1 = y;
    }
    y2 = Math.max(y, y2);
    points = points.concat({ x: x - x1, y: y - y1 });
    return { points, x1, y1, x2, y2 };
}

export function convertPointsToPath(points: ReadonlyArray<Point>): string {
    return 'M' +
        points.map(p => `${p.x} ${p.y}`)
            .join('L');
}

export function resizePoints(points: ReadonlyArray<Point>,
                             startX1: number, startY1: number, startX2: number, startY2: number,
                             endX1: number, endY1: number, endX2: number, endY2: number
): ReadonlyArray<Point> {
    const xScale = (endX2 - endX1) / (startX2 - startX1);
    const yScale = (endY2 - endY1) / (startY2 - startY1);
    return points.map(p => ({ x: p.x * xScale, y: p.y * yScale}))
}

export function reducePoints(points: ReadonlyArray<Point>): ReadonlyArray<Point> {
    return reduceSegment(points, 0, points.length);
}

function reduceSegment(points: ReadonlyArray<Point>, first: number, last: number): ReadonlyArray<Point> {
    const DELTA = 2;
    const n = last - first;
    let max = 0;
    let index = 0;

    switch (n) {
        case 1:
            return [ points[first] ];
        case 2:
            return [ points[first], points[first + 1]];
    }

    const segment = prepareLineSegment(points[first], points[last - 1]);

    // Find the point with the maximum distance
    for (let i = first + 1; i < last - 1; i++) {
        const d = distancePointToSegment(points[i], segment);
        if (d > max) {
            index = i;
            max = d;
        }
    }

    if (max > DELTA) {
        const result1 = reduceSegment(points, first, index + 1);
        const result2 = reduceSegment(points, index, last);
        return result1.slice(0, -1).concat(result2);
    } else {
        return [ points[first], points[last - 1] ];
    }

}

function prepareLineSegment(start: Point, end: Point) {
    const cx = (start.x + end.x) / 2;
    const cy = (start.y + end.y) / 2;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const l = Math.max(Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2)), 1e-12);
    const ux = dx / l;
    const uy = dy / l;
    const rUx = end.x - cx;
    const rUy = end.y - cy;
    let r = Math.abs(ux) > Math.abs(uy)? rUx / ux : rUy / uy;

    if (isNaN(r)) {
        r = 0;
    }
    return {
        start,
        end,
        cx,
        cy,
        ux,
        uy,
        r,
        rUx,
        rUy,
    };
}

function distancePointToSegment(point: Point, segment: any): number {
    let diffX = point.x - segment.cx;
    let diffY = point.y - segment.cy;
    const t = segment.ux * diffX + segment.uy * diffY;

    if (t < -segment.r) {
        diffX += segment.rUx;
        diffY += segment.rUy;
    } else if (t > segment.r) {
        diffX -= segment.rUx;
        diffY -= segment.rUy;
    } else {
        diffX -= t * segment.ux;
        diffY -= t * segment.uy;
    }
    return Math.pow(diffX, 2) + Math.pow(diffY, 2);
}

export function createArrowPath(x1: number, y1: number, x2: number, y2: number, mainRandom: LineRandom, arm1Random: LineRandom, arm2Random: LineRandom): string {
    const ax = x2 - x1;
    const ay = y2 - y1;
    if (Math.abs(ax) < EPSILON && Math.abs(ay) < EPSILON) {
        return '';
    }
    const aLength = Math.sqrt(ax * ax + ay * ay);
    const arm1x = (-ax + ay) / 2;
    const arm1y = (-ay - ax) / 2;
    const rawArmLength = Math.sqrt(arm1x * arm1x + arm1y * arm1y);
    const x3 = x2 + arm1x * aLength / rawArmLength / 6;
    const y3 = y2 + arm1y * aLength / rawArmLength / 6;
    const arm2x = (-ax - ay) / 2;
    const arm2y = (-ay + ax) / 2;
    const x4 = x2 + arm2x * aLength / rawArmLength / 6;
    const y4 = y2 + arm2y * aLength / rawArmLength / 6;
    return createLinePath(x1, y1, x2, y2, mainRandom)
        + createLinePath(x3, y3, x2, y2, arm1Random)
        + createSectionPath(x2, y2, x4, y4, arm2Random);
}



// WEBPACK FOOTER //
// ./src/services/shape-service.ts